import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, Scale } from 'lucide-react';
import {
  disputesService,
  type DisputePriority,
  type DisputeStatus,
  type DisputeType,
} from '@services/disputes.service';
import { cn, formatCurrency } from '@lib/utils';

// ── Badge token maps ───────────────────────────────────────────────────────
const statusChip: Record<DisputeStatus, string> = {
  open: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  under_review: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  awaiting_merchant_response: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  resolved: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  closed: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const statusLabel: Record<DisputeStatus, string> = {
  open: 'Open',
  under_review: 'Under Review',
  awaiting_merchant_response: 'Awaiting Merchant',
  resolved: 'Resolved',
  closed: 'Closed',
};

const priorityChip: Record<DisputePriority, string> = {
  urgent: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  high: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  medium: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  low: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
};

const typeLabel: Record<DisputeType, string> = {
  product_not_received: 'Not Received',
  damaged_product: 'Damaged Product',
  wrong_product: 'Wrong Product',
  missing_items: 'Missing Items',
  poor_quality: 'Poor Quality',
  other: 'Other',
};

// ── Deadline helpers ───────────────────────────────────────────────────────
// Breached deadlines read as danger; anything inside 12h reads as warning.
// Resolved/closed disputes no longer race a deadline.
function deadlineState(deadline: string | null, status: DisputeStatus): 'none' | 'ok' | 'near' | 'breached' {
  if (!deadline || status === 'resolved' || status === 'closed') return 'none';
  const msLeft = new Date(deadline).getTime() - Date.now();
  if (msLeft < 0) return 'breached';
  if (msLeft < 12 * 60 * 60 * 1000) return 'near';
  return 'ok';
}

export function deadlineText(deadline: string | null, status: DisputeStatus): string {
  if (!deadline) return '—';
  const state = deadlineState(deadline, status);
  if (state === 'none') return new Date(deadline).toLocaleDateString();
  const msLeft = new Date(deadline).getTime() - Date.now();
  if (state === 'breached') return 'Overdue';
  const hours = Math.floor(msLeft / (1000 * 60 * 60));
  const minutes = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m left`;
}

const deadlineClass: Record<ReturnType<typeof deadlineState>, string> = {
  breached: 'text-[var(--state-danger)] font-semibold',
  near: 'text-[var(--state-warning)] font-semibold',
  ok: 'text-[var(--state-success)] font-semibold',
  none: 'text-[var(--text-muted)]',
};

export const DisputeQueuePage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<'' | DisputeStatus>('');
  const [priorityFilter, setPriorityFilter] = useState<'' | DisputePriority>('');

  const { data: disputes, isLoading, error } = useQuery({
    queryKey: ['disputes', statusFilter, priorityFilter],
    queryFn: () =>
      disputesService.list({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
      }),
  });

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
      {/* Page header */}
      <div className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          Platform
        </p>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
          Disputes
        </h1>
        <p className="text-[14px] text-[var(--text-muted)]">
          Customer–merchant dispute resolution queue
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | DisputeStatus)}
          className="w-56 rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="under_review">Under Review</option>
          <option value="awaiting_merchant_response">Awaiting Merchant</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as '' | DisputePriority)}
          className="w-52 rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
        >
          <option value="">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Table card */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        {isLoading && (
          <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
            Loading disputes...
          </div>
        )}

        {!!error && !isLoading && (
          <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
            Failed to load disputes.
          </div>
        )}

        {disputes && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Subject
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Type
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Priority
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Amount
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Raised
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Deadline
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {disputes.map((dispute) => {
                  const dState = deadlineState(dispute.deadline, dispute.status);
                  return (
                    <tr
                      key={dispute.id}
                      onClick={() => navigate(`/disputes/${dispute.id}`)}
                      className="hover:bg-[var(--bg-base)] transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Scale className="w-5 h-5 text-[var(--text-muted)] shrink-0" strokeWidth={1.5} />
                          <div className="min-w-0">
                            <div className="text-[14px] font-medium text-[var(--text-primary)] truncate max-w-[320px]">
                              {dispute.subject}
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)]">
                              Order {dispute.orderId.slice(0, 8)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="rounded-full bg-[var(--bg-base)] px-2.5 py-1 text-[11px] font-semibold text-[var(--text-secondary)]">
                          {typeLabel[dispute.disputeType] ?? dispute.disputeType}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                            statusChip[dispute.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {statusLabel[dispute.status] ?? dispute.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                            priorityChip[dispute.priority] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {dispute.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[14px] font-medium text-[var(--text-primary)]">
                        {formatCurrency(dispute.disputedAmount)}
                      </td>
                      <td className="px-6 py-4 text-[14px] text-[var(--text-muted)]">
                        {new Date(dispute.raisedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn('inline-flex items-center gap-1.5 text-[13px]', deadlineClass[dState])}>
                          {dState !== 'none' && <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />}
                          {deadlineText(dispute.deadline, dispute.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {disputes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]">
                      No disputes found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
